import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ToastState } from '@/lib/types';

export type ToastTone = 'success' | 'info' | 'warning' | 'error';

export interface ToastItem {
  id: number;
  message: string;
  description?: string;
  tone: ToastTone;
  duration: number;
  leaving?: boolean;
}

export interface NotifyOptions {
  description?: string;
  tone?: ToastTone;
  duration?: number;
}

interface ToastContextValue {
  /** Latest toast (back-compat for old single-toast readers). */
  toast: ToastState;
  /** Full queue rendered by <ToastStack />. */
  toasts: ToastItem[];
  /** Back-compat setter — routes into the queue. */
  setToast: (toast: ToastState) => void;
  notify: (message: string, opts?: NotifyOptions | ToastTone) => number;
  success: (message: string, opts?: Omit<NotifyOptions, 'tone'>) => number;
  info: (message: string, opts?: Omit<NotifyOptions, 'tone'>) => number;
  warning: (message: string, opts?: Omit<NotifyOptions, 'tone'>) => number;
  error: (message: string, opts?: Omit<NotifyOptions, 'tone'>) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

const ToastContext = createContext<ToastContextValue>({
  toast: null,
  toasts: [],
  setToast: () => {},
  notify: () => 0,
  success: () => 0,
  info: () => 0,
  warning: () => 0,
  error: () => 0,
  dismiss: () => {},
  clear: () => {},
});

let nextId = 1;

const EXIT_MS = 320;

function normalizeOpts(opts?: NotifyOptions | ToastTone): NotifyOptions {
  if (typeof opts === 'string') return { tone: opts };
  return opts ?? {};
}

function durationFor(tone: ToastTone, explicit?: number): number {
  if (typeof explicit === 'number') return explicit;
  return tone === 'error' ? 5200 : tone === 'warning' ? 4200 : 3400;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    timers.current.forEach((timer, key) => {
      if (key === id || key === id * -1) {
        clearTimeout(timer);
        timers.current.delete(key);
      }
    });
    setToasts(current => current.filter(t => t.id !== id));
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      setToasts(current => {
        if (!current.some(t => t.id === id && !t.leaving)) return current;
        return current.map(t => (t.id === id ? { ...t, leaving: true } : t));
      });
      if (timers.current.has(id * -1)) return;
      const exitTimer = setTimeout(() => {
        timers.current.delete(id * -1);
        remove(id);
      }, EXIT_MS);
      timers.current.set(id * -1, exitTimer);
    },
    [remove],
  );

  const notify = useCallback(
    (message: string, opts?: NotifyOptions | ToastTone): number => {
      const text = (message ?? '').trim();
      if (!text) return 0;
      const parsed = normalizeOpts(opts);
      const tone = parsed.tone ?? 'success';
      const id = nextId++;
      const item: ToastItem = {
        id,
        message: text,
        description: parsed.description,
        tone,
        duration: durationFor(tone, parsed.duration),
      };
      setToasts(current => [...current.slice(-3), item]);
      const timer = setTimeout(() => {
        timers.current.delete(id);
        dismiss(id);
      }, item.duration);
      timers.current.set(id, timer);
      return id;
    },
    [dismiss],
  );

  const success = useCallback(
    (message: string, opts?: Omit<NotifyOptions, 'tone'>) =>
      notify(message, { ...opts, tone: 'success' }),
    [notify],
  );
  const info = useCallback(
    (message: string, opts?: Omit<NotifyOptions, 'tone'>) =>
      notify(message, { ...opts, tone: 'info' }),
    [notify],
  );
  const warning = useCallback(
    (message: string, opts?: Omit<NotifyOptions, 'tone'>) =>
      notify(message, { ...opts, tone: 'warning' }),
    [notify],
  );
  const error = useCallback(
    (message: string, opts?: Omit<NotifyOptions, 'tone'>) =>
      notify(message, { ...opts, tone: 'error' }),
    [notify],
  );

  const clear = useCallback(() => {
    timers.current.forEach(timer => clearTimeout(timer));
    timers.current.clear();
    setToasts([]);
  }, []);

  /** Back-compat: old `setToast({ message, tone })` calls animate through the queue. */
  const setToast = useCallback(
    (toast: ToastState) => {
      if (!toast) {
        clear();
        return;
      }
      notify(toast.message, (toast.tone as ToastTone) ?? 'info');
    },
    [clear, notify],
  );

  // Global bridge: non-React code (api client, sagas) can fire
  // `window.dispatchEvent(new CustomEvent('sajilo:toast', { detail }))`.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { message?: string; description?: string; tone?: ToastTone; duration?: number }
        | null;
      if (!detail?.message) return;
      notify(detail.message, {
        description: detail.description,
        tone: detail.tone ?? 'success',
        duration: detail.duration,
      });
    };
    window.addEventListener('sajilo:toast', handler as EventListener);
    return () => window.removeEventListener('sajilo:toast', handler as EventListener);
  }, [notify]);

  // Action-button bridge: any `<button data-toast="Saved!">` auto-toasts on click.
  // Optional: `data-toast-tone="info"` + `data-toast-description="..."`.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const el = target?.closest?.('[data-toast]') as HTMLElement | null;
      if (!el) return;
      const message = el.getAttribute('data-toast')?.trim();
      if (!message) return;
      const tone = (el.getAttribute('data-toast-tone') as ToastTone) || 'success';
      const description = el.getAttribute('data-toast-description') || undefined;
      notify(message, { tone, description });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [notify]);

  useEffect(
    () => () => {
      timers.current.forEach(timer => clearTimeout(timer));
      timers.current.clear();
    },
    [],
  );

  const latest = toasts.length > 0 ? toasts[toasts.length - 1] : null;
  const toast: ToastState = latest
    ? {
        message: latest.description
          ? `${latest.message} — ${latest.description}`
          : latest.message,
        tone: latest.tone,
      }
    : null;
  return (
    <ToastContext.Provider
      value={{ toast, toasts, setToast, notify, success, info, warning, error, dismiss, clear }}
    >
      {children}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/** Fire a toast from outside React (services, sagas, utils). */
export function emitToast(detail: {
  message: string;
  description?: string;
  tone?: ToastTone;
  duration?: number;
}): void {
  try {
    window.dispatchEvent(new CustomEvent('sajilo:toast', { detail }));
  } catch {
    /* non-DOM environment — ignore */
  }
}
